import { Fragment, forwardRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

import {
  HAIRLINE_DIVIDER_CLASSES,
  MENU_BUTTON_CLASSES
} from './ProfileMenuTheme';
import { app as $appContent } from '../../content';
import { clsm } from '../../utils';
import { createAnimationProps } from '../../helpers/animationPropsHelper';
import { LogoutDarkNew, LogoutLightNew } from '../../assets/icons';
import { useUser } from '../../contexts/User';
import Button from '../Button';
import UserAvatar from '../UserAvatar';
import withPortal from '../withPortal';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/Theme';

const $content = $appContent.navbar;

const Popup = forwardRef(
  ({ navData, toggleProfileMenu, menuClassName }, ref) => {
    const navigate = useNavigate();
    const { logOut, userData } = useUser();
    const { isDark } = useTheme();
    const { given_name: username, family_name: lastName } = userData || {};
    const { userRole } = userData || {};
    const avatarSrc = '';
    const profileColor = 'default';
    const hasAvatar = !!avatarSrc;

    const commonMenuButtonProps = useMemo(
      () => ({
        variant: 'tertiaryText',
        className: clsm(MENU_BUTTON_CLASSES),
        onClick: () => toggleProfileMenu(false)
      }),
      [toggleProfileMenu]
    );

    return (
      <motion.div
        {...createAnimationProps({ animations: ['fadeIn-half', 'scale'] })}
        className={clsm([
          'px-1',
          'rounded-3xl',
          'dark:bg-[#2A2B32]',
          'dark:border-black dark:border',
          'bg-lightMode-gray-light',
          'no-scrollbar',
          'overflow-y-auto',
          'overflow-x-hidden',
          'supports-overlay:overflow-y-overlay',
          menuClassName
        ])}
        ref={ref}
      >
        <div
          className={clsm([
            'w-full',
            'h-full',
            'px-3',
            'py-4',
            'flex',
            'flex-col',
            'space-y-4',
            'overflow-y-auto',
            'overflow-x-hidden',
            'supports-overlay:overflow-y-overlay'
          ])}
        >
          <div
            {...commonMenuButtonProps}
            className={clsm(
              // MENU_BUTTON_CLASSES,
              [
                'w-full',
                'flex',
                'items-center',
                'space-x-3',
                'px-3',
                'py-2.5',
                'h-auto',
                '[&>img]:hover:rounded-lg',
                '[&>img]:focus:rounded-lg'
              ],
              !hasAvatar && [
                '[&>div]:bg-lightMode-gray-light',
                '[&>div]:hover:bg-lightMode-gray-light-hover',
                'dark:[&>div]:bg-darkMode-gray',
                'dark:[&>div]:hover:bg-darkMode-gray-hover'
              ]
            )}
            data-testid="profileMenu-avatar"
            type="button"
          >
            <UserAvatar
              avatarSrc={avatarSrc}
              profileColor={profileColor}
              userName={`${username} ${lastName}`}
            />
            <p className="truncate text-black dark:text-white">
              {`${username} ${lastName}` || $content.profile}
            </p>
          </div>
          <span className={clsm(HAIRLINE_DIVIDER_CLASSES)} />

          {/* CUSTOM NAVIGATION BUTTONS */}
          {navData.map(
            ({ icon, label, to, hasDivider, pageName, allowedRoles }) => {
              const hasRequiredRole =
                (allowedRoles && allowedRoles.length === 0) ||
                (userRole && allowedRoles && allowedRoles.includes(userRole));
              return (
                hasRequiredRole && (
                  <Fragment key={label}>
                    <Button
                      {...commonMenuButtonProps}
                      data-testid={`${pageName}-button`}
                      type="nav"
                      to={to}
                    >
                      {icon}
                      <p className="truncate">{label}</p>
                    </Button>
                    {hasDivider && (
                      <span className={clsm(HAIRLINE_DIVIDER_CLASSES)} />
                    )}
                  </Fragment>
                )
              );
            }
          )}

          {/* LOGOUT BUTTON */}
          <Button
            {...commonMenuButtonProps}
            onClick={() => {
              logOut('logOut');
              navigate('/login');
            }}
          >
            <span>{isDark ? <LogoutDarkNew /> : <LogoutLightNew />}</span>
            <p className="truncate">{$content.log_out}</p>
          </Button>
        </div>
      </motion.div>
    );
  }
);

const PortalPopup = withPortal(Popup, 'profile-menu-popup', {
  baseContainerClasses: clsm(['absolute', 'z-[1000]'])
});

const MenuPopup = forwardRef(({ asPortal, isOpen, ...restProps }, ref) =>
  asPortal ? (
    <PortalPopup isOpen={isOpen} ref={ref} {...restProps} />
  ) : (
    isOpen && <Popup ref={ref} {...restProps} />
  )
);

MenuPopup.defaultProps = {
  asPortal: false,
  isOpen: false
};

MenuPopup.propTypes = {
  asPortal: PropTypes.bool,
  isOpen: PropTypes.bool
};

Popup.defaultProps = {
  isOpen: false,
  menuClassName: '',
  navData: []
};

Popup.propTypes = {
  isOpen: PropTypes.bool,
  navData: PropTypes.array,
  menuClassName: PropTypes.string,
  toggleProfileMenu: PropTypes.func.isRequired
};

export default MenuPopup;
